// What a spoken command does, wherever it was asked for: the spoken agent's
// device tools in the Shell, and a typed turn's device calls in Prime's
// thread. Where "open" goes and how a reply is shown come in as the
// environment; the rest is one implementation: the player, capture, the
// pomodoro, the day's task.
import { api } from '@/runtime'
import { addTaskFromText } from '@/components/QuickAdd'
import { taskActions } from '@/lib/actions'
import { usePlayer } from '@/state/player'
import { usePomodoro } from '@/state/pomodoro'
import type { ProjectResource } from '@/kinds/project'
import type { TaskResource } from '@/kinds/task'

/** What a device tool asks for, as `useVoiceAgent` maps the agent's calls. */
export type Command =
  | { kind: 'capture'; text: string }
  | { kind: 'play'; what: string; sort?: 'track' | 'album' | 'artist' | 'playlist'; device?: string }
  | { kind: 'transport'; action: 'play' | 'pause' | 'next' | 'previous' }
  | { kind: 'pomodoro'; action: 'start' | 'pause' | 'skip' | 'reset' }
  | { kind: 'start' }
  | { kind: 'done' }
  | { kind: 'open'; view: string }
  | { kind: 'whatnext' }
  | { kind: 'ask'; text: string }

export interface VoiceEnv {
  /** show (and, without the agent, speak) a line back to the user */
  say: (text: string) => void
  /** show a screen by name; false when this surface has no such screen */
  open: (view: string) => boolean
  today: { hero?: TaskResource; running?: TaskResource; rest: TaskResource[] }
  projects: ProjectResource[]
  /** a question for the agent, where the surface has a text conversation */
  ask?: (text: string) => Promise<void>
}

/** The screens the agent may ask for, on either surface. Keep in step with the `open` tool's enum on the server. */
export const OPENABLE = [
  'today',
  'inbox',
  'upcoming',
  'someday',
  'projects',
  'pages',
  'lists',
  'calendar',
  'review',
  'log',
  'recurring',
  'captures',
  'library',
  'music',
  'playlists',
  'focus',
  'agenda',
  'capture',
  'weather',
  'home',
  'lyrics',
  'search',
  'ask',
  'agents',
  'apps',
  'settings',
  'desk',
] as const

/**
 * Where each screen lives in the app. The desk's old screens (weather, the
 * house, lyrics, the desk itself) are widgets on Home now (2026-10-10).
 */
export const APP_ROUTES: Record<string, string> = {
  today: '/',
  inbox: '/inbox',
  upcoming: '/upcoming',
  someday: '/someday',
  projects: '/projects',
  pages: '/pages',
  lists: '/lists',
  calendar: '/calendar',
  review: '/review',
  log: '/log',
  recurring: '/recurring',
  captures: '/captures',
  library: '/library',
  music: '/music',
  playlists: '/music/playlists',
  focus: '/focus',
  agenda: '/upcoming',
  capture: '/inbox',
  search: '/',
  ask: '/agents',
  agents: '/agents',
  settings: '/settings',
  desk: '/home',
  weather: '/home',
  lyrics: '/home',
  wellbeing: '/wellbeing',
  reset: '/reset',
  chair: '/reset',
  checkin: '/checkin',
  people: '/people',
  dates: '/dates',
  history: '/log',
  trash: '/trash',
  ahead: '/upcoming',
  notes: '/pages',
  // the home screens, where the house widget is
  home: '/home',
}

export const runVoiceCommand = async (c: Command, env: VoiceEnv): Promise<void> => {
  const { say, today, projects } = env
  const control = usePlayer.getState().control
  switch (c.kind) {
    case 'transport':
      await control(c.action).catch((e: Error) => say(e.message))
      return
    case 'play': {
      try {
        // a named device: match it by name against what Spotify lists as open
        let deviceId: string | undefined
        let deviceName = ''
        if (c.device) {
          const { devices } = await api.spotifyDevices()
          const n = c.device.toLowerCase()
          const words = n.split(/\s+/).filter((w) => w.length > 2 && !['the', 'my', 'our'].includes(w))
          const hit =
            devices.find((d) => d.name.toLowerCase().includes(n)) ??
            devices.find((d) => words.some((w) => d.name.toLowerCase().includes(w)))
          if (!hit) {
            say(
              `No Spotify device called ${c.device} is open. Open are: ${devices.map((d) => d.name).join(', ') || 'none'}.`,
            )
            return
          }
          deviceId = hit.id
          deviceName = hit.name
        }
        if (!c.what) {
          await control('play', { device_id: deviceId })
        } else {
          const r = await api.spotifySearch(c.what)
          // a genre or mood is a playlist; the model says which sort it meant
          const want =
            c.sort ?? (/\b(some|something|music|vibes?|mix)\b/i.test(c.what) ? 'playlist' : 'track')
          const hit = r.hits.find((h) => h.kind === want) ?? r.hits[0]
          if (!hit) {
            say(`Nothing found for ${c.what}.`)
            return
          }
          // straight to the remote: an error here is worth hearing, not hiding behind a preview
          await control('play', {
            uri: hit.kind === 'track' ? hit.uri : undefined,
            context_uri: hit.kind === 'track' ? undefined : hit.uri,
            device_id: deviceId,
          })
          deviceName = deviceName || usePlayer.getState().state?.device || ''
          say(
            `Playing ${hit.kind === 'track' ? hit.title : `the ${hit.kind} ${hit.title}`}${hit.artist ? ` by ${hit.artist}` : ''}${deviceName ? ` on ${deviceName}` : ''}.`,
          )
          return
        }
        say(`Playing${deviceName ? ` on ${deviceName}` : ''}.`)
      } catch (e) {
        say(e instanceof Error ? e.message : 'Spotify refused')
      }
      return
    }
    case 'capture': {
      // the same door as the phone's Shortcut: the server files it, with the assistant when triage is on
      try {
        await api.capture(c.text, 'voice')
        say(`Captured: ${c.text}`)
      } catch {
        await addTaskFromText(c.text, projects)
        say(`Added ${c.text}.`)
      }
      return
    }
    case 'pomodoro': {
      const pm = usePomodoro.getState()
      if (c.action === 'start') {
        const task = today.running ?? today.hero
        pm.start(task ? { id: task.id, title: task.spec.title } : null)
        say(
          task
            ? `Focus on ${task.spec.title}. ${pm.config.work} minutes.`
            : `Focus. ${pm.config.work} minutes.`,
        )
      } else if (c.action === 'pause') {
        pm.pause()
        say('Paused.')
      } else if (c.action === 'skip') {
        pm.skip()
        say('Skipped.')
      } else {
        pm.reset()
        say('Reset.')
      }
      env.open('focus')
      return
    }
    case 'start':
      if (today.hero) {
        await taskActions.start(today.hero)
        say(`Started ${today.hero.spec.title}.`)
      } else say('Nothing on today to start.')
      return
    case 'done':
      if (today.running) {
        await taskActions.complete(today.running)
        say(`Done: ${today.running.spec.title}.`)
      } else if (today.hero) {
        await taskActions.complete(today.hero)
        say(`Done: ${today.hero.spec.title}.`)
      } else say('Nothing to finish.')
      return
    case 'whatnext':
      say(
        today.hero
          ? `Next: ${today.hero.spec.title}${today.rest.length ? `, then ${today.rest[0]!.spec.title}` : ''}.`
          : 'Nothing on today.',
      )
      return
    case 'open':
      if (!env.open(c.view)) say(`There is no ${c.view} screen here.`)
      else say(`Opened ${c.view}.`)
      return
    case 'ask':
      if (env.ask) {
        env.open('ask')
        await env.ask(c.text)
      } else say('Ask me out loud and I will answer.')
      return
  }
}
